import type { ReactNode } from 'react';
import { BenzeneIcon, TrashIcon } from '@alchemiesolns/ui';
import './MoleculeEditToolbar.css';

export interface MoleculeEditToolbarProps {
  label: string;
  /** Blank canvas when there is no structure; combined with Edit below */
  onCreateSVG: () => void;
  onAddText: () => void;
  /** Inserted after Add Text (e.g. “Move to” transfer). */
  afterAddText?: ReactNode;
  configString?: string;
  onRemoveConfig?: () => void;
  onEditExisting?: () => void;
  overlayText?: string;
  onRemoveText?: () => void;
}

function AddTextGlyph() {
  return (
    <svg
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <polyline points="4 7 4 4 20 4 20 7" />
      <line x1="9" y1="20" x2="15" y2="20" />
      <line x1="12" y1="4" x2="12" y2="20" />
    </svg>
  );
}

/**
 * Inline preview toolbar: structure (build + edit unified), text, removals.
 */
export function MoleculeEditToolbar({
  label,
  onCreateSVG,
  onAddText,
  afterAddText,
  configString,
  onRemoveConfig,
  onEditExisting,
  overlayText,
  onRemoveText,
}: MoleculeEditToolbarProps) {
  const hasStructure = Boolean(configString?.trim());

  const handleStructureClick = () => {
    if (hasStructure && onEditExisting) {
      onEditExisting();
      return;
    }
    onCreateSVG();
  };

  return (
    <div className="molecule-edit-toolbar" role="toolbar" aria-label={`${label} editing actions`}>
      <button
        type="button"
        className="molecule-edit-toolbar__btn"
        onClick={handleStructureClick}
        aria-label={`Edit ${label} structure`}
        title="Edit"
      >
        <BenzeneIcon />
      </button>

      <button
        type="button"
        className="molecule-edit-toolbar__btn"
        onClick={onAddText}
        aria-label={`Add Text (${label})`}
        title="Add Text"
      >
        <AddTextGlyph />
      </button>

      {afterAddText}

      {hasStructure && onRemoveConfig && (
        <button
          type="button"
          className="molecule-edit-toolbar__btn molecule-edit-toolbar__btn--danger"
          onClick={onRemoveConfig}
          aria-label={`Remove ${label} structure`}
          title="Remove structure"
        >
          <TrashIcon />
        </button>
      )}

      {overlayText && onRemoveText && (
        <button
          type="button"
          className="molecule-edit-toolbar__btn molecule-edit-toolbar__btn--danger"
          onClick={onRemoveText}
          aria-label={`Remove ${label} text`}
          title="Remove text overlay"
        >
          <TrashIcon />
        </button>
      )}
    </div>
  );
}
